import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { faNote } from '@fortawesome/pro-solid-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize } from 'lodash';

import moduleHeaderIconsStyles from 'features/submissions/components/molecules/ModuleHeaderIcons/moduleHeaderIconsStyles';
import Chip from 'shared/components/atoms/Chip/Chip';
import Tooltip from 'shared/components/atoms/Tooltip';
import { Flex } from 'shared/components/display';

import { SUBMISSION_COPY } from 'shared/config/copy';
import COLORS from 'shared/styles/colors';
import { breakpoints } from 'shared/styles/styleFunctions';
import { Revision, RevisionStatus } from 'shared/typings/revisions';
import { ModuleSubmissionStatus } from 'shared/typings/submissions';

interface Props {
  status: ModuleSubmissionStatus;
  isModuleLoading: boolean;
  revisions: Revision[] | undefined;
  onClick: () => void;
  moduleNum: number;
  isCreator?: boolean;
}

const ModuleHeaderIcons: React.FC<Props> = ({
  status,
  isModuleLoading,
  revisions,
  onClick,
  moduleNum,
  isCreator = false,
}) => {
  const isMobile = useMediaQuery({ maxWidth: breakpoints.md - 1 });

  const hasOngoingRevisions = revisions?.some((rev) => rev.status === RevisionStatus.DOING);
  const hasCompletedRevisions = revisions?.some((rev) => rev.status === RevisionStatus.DONE);

  const showRevisionsIconForMarketer =
    [ModuleSubmissionStatus.REVISION, ModuleSubmissionStatus.REVIEW].includes(status) &&
    revisions &&
    revisions.length > 0;
  const revisionsIconColorMarketer = hasOngoingRevisions ? COLORS.RED200 : COLORS.GREEN500;

  // when in REVIEW, only show revisions icon if there are revisions that are done
  const showRevisionsIconForCreator =
    (status === ModuleSubmissionStatus.REVIEW && hasCompletedRevisions) ||
    (status === ModuleSubmissionStatus.REVISION && revisions && revisions.length > 0);
  const revisionsIconColorCreator =
    status === ModuleSubmissionStatus.REVISION && hasOngoingRevisions ? COLORS.RED200 : COLORS.GREEN500;

  return (
    <Flex className="module-header-icons" css={moduleHeaderIconsStyles}>
      <Tooltip
        content={SUBMISSION_COPY[`MODULE_${status}_TOOLTIP`]}
        as="span"
        arrow
        placement={isMobile ? 'bottom-end' : 'right'}
        offset={isMobile ? [10, 10] : [5, 20]}
      >
        <Chip
          label={capitalize(status.toLowerCase())}
          color={COLORS.PRIMARY400}
          bgColor={COLORS.PRIMARY100}
          dataCy={`module-header-icons__status-${moduleNum}`}
          square
        />
      </Tooltip>
      {isModuleLoading ? (
        <FontAwesomeIcon
          icon={faCircleNotch}
          color={COLORS.PRIMARY500}
          data-cy={`module-header-icons__loading-${moduleNum}`}
        />
      ) : (
        <>
          {(isCreator ? showRevisionsIconForCreator : showRevisionsIconForMarketer) && (
            <Tooltip
              content={SUBMISSION_COPY.MODULE_REVIEW_ICON_TOOLTIP}
              as="span"
              arrow
              placement={isMobile ? 'bottom-end' : 'right'}
              offset={isMobile ? [10, 10] : [5, 20]}
            >
              <FontAwesomeIcon
                className="module-header-icons__note"
                icon={faNote}
                color={isCreator ? revisionsIconColorCreator : revisionsIconColorMarketer}
                onClick={onClick}
              />
            </Tooltip>
          )}
        </>
      )}
    </Flex>
  );
};
export default ModuleHeaderIcons;
